import React, { useMemo, useState, useEffect, useCallback } from "react";

import { connect } from "react-redux";
import { createGlobalStyle } from "styled-components";

import { getReferencesForReports } from "actions/references";
import { servicePlanNoTrunkFilter } from "utils/filters";
import { getAccessList } from "actions/access_list";
import {
    EUR_DEFAULT_CURRENCY_KEY,
    USD_DEFAULT_CURRENCY_KEY,
    LOCAL_STORAGE_EXTENDED_ACCESS_LIST_FILTERS
} from "const";
import {
    ACCESS_LIST_GET_LIST_API,
    SMS_ACCESS_LIST_GET_LIST_API,
    ACCESS_LIST_EXTENDED_GET_LIST_API,
    SMS_ACCESS_LIST_EXTENDED_GET_LIST_API
} from "const/apiMethods";
import { LS_ACCESS_LIST_PER_PAGE } from "const/localStorageKeys";
import { useWindowWidth } from "hooks";
import { servicePick, resolvePerPage } from "utils";
import PanelLayout from "components/base/PanelLayout";
import PageHeader from "components/base/PageHeader";
import ExportCsv from "components/ExportCsv";
import AccessListFilters from "./AccessListFilters";
import AccessListTable from "./AccessListTable";


import { FlexboxGrid } from "rsuite";

const AccessList = ({
    page,
    count,
    perPage,
    service,
    accessList,
    accessListLoading,
    currency_list,
    paymentTermsList,
    supplierList,
    service_plan_list,
    getAccessList,
    getReferencesForReports
}) => {

    const [extended, setExtended] = useState(false);
    const minAtxDialersName = servicePick(service, "min_atx_numbers", "min_dialer_numbers");

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 768;

    const defaultNotExtendedFilter = {
        [servicePick(service, "str", "origin")]: "",
        [servicePick(service, "str2", "destination")]: "",
        no_number: false,
        show_accesses_without_capacity: false,
        sp_key_list: [],
        cur_key: servicePick(service, USD_DEFAULT_CURRENCY_KEY, EUR_DEFAULT_CURRENCY_KEY),
        supplier_id_list: []
    };

    const defaultExtendedFilter = {
        [servicePick(service, "originator", "supplier_id_list")]: servicePick(service, "", []),
        [servicePick(service, "origin", "senderid")]: "",
        destination: "",
        exclude_local_traffic: false,
        rate: null,
        min_numbers: null,
        [minAtxDialersName]: null
    };

    const localStorageExtendedFilters = localStorage.getItem(LOCAL_STORAGE_EXTENDED_ACCESS_LIST_FILTERS);
    const savedExtendedFilter = localStorageExtendedFilters ? JSON.parse(localStorageExtendedFilters) : {};
    const extendedDefaultFilter = {
        ...defaultExtendedFilter,
        min_numbers: savedExtendedFilter.min_numbers,
        [minAtxDialersName]: savedExtendedFilter[minAtxDialersName]
    };

    const defaultFilter = extended ? extendedDefaultFilter : defaultNotExtendedFilter;

    const [filters, setFilters] = useState(defaultFilter);
    const defaultNotExtendedSorting = servicePick(service,
        {
            column: "access_origin",
            type: "asc"
        },
        {
            column: "origin",
            type: "asc"
        }
    );
    const defaultExtendedSorting = servicePick(service,
        {
            column: "origin",
            type: "asc"
        },
        {
            column: "supplier",
            type: "asc"
        }
    );

    let defaultSort = extended ? defaultExtendedSorting : defaultNotExtendedSorting;

    const [sortState, setSortState] = useState(defaultSort);

    const resolvedPerPage = useMemo(() => resolvePerPage(
        LS_ACCESS_LIST_PER_PAGE,
        perPage,
    ), [perPage]);

    const [currentPerPage, setCurrentPerPage] = useState(resolvedPerPage);

    useEffect(() => {
        setCurrentPerPage(resolvedPerPage);
    }, [resolvedPerPage]);

    useEffect(() => {
        setFilters(defaultFilter);

        if (!service) {
            getReferencesForReports();
        }
    }, [service]);

    useEffect(() => {
        const filter = extended ? extendedDefaultFilter : defaultFilter;

        getAccessList(filter, service, 1, resolvedPerPage, defaultSort, false, extended);
        setSortState(defaultSort);
        setFilters(defaultFilter);
    }, [extended]);


    const onChangeFilters = (filter) => {
        const filterOnlyExist = Object.keys(filter).reduce((result, key) => {
            if (key in defaultFilter) {
                result[key] = filter[key];
            }
            return result;
        }, {});

        if (extended) {
            const alternateServiceFilerData = {
                min_atx_numbers: filterOnlyExist[minAtxDialersName],
                min_dialer_numbers: filterOnlyExist[minAtxDialersName]
            };

            const filterDataToSave = {
                ...filterOnlyExist,
                ...alternateServiceFilerData
            };

            localStorage.setItem(LOCAL_STORAGE_EXTENDED_ACCESS_LIST_FILTERS, JSON.stringify(filterDataToSave));
        }

        setFilters({ ...filterOnlyExist });
        getAccessList(filterOnlyExist, service, 1, currentPerPage, sortState, false, extended);
    };

    const getItems = (_page, _perPage = currentPerPage, _sort) => {
        getAccessList(filters, service, _page, _perPage, _sort, false, extended);
    };

    const { sp_key: downaloadSpKey, ...remainingDownloadFilters } = filters;
    const downloadSpKeyList = !remainingDownloadFilters.sp_key_list
        || !remainingDownloadFilters.sp_key_list.length
        ? null
        : remainingDownloadFilters.sp_key_list;

    const getDownloadParams = useCallback(() => {
        const curMinRate = remainingDownloadFilters["rate"];
        const curMinNumbers = remainingDownloadFilters["min_numbers"];
        const curMinAtxNumbers = remainingDownloadFilters[minAtxDialersName];
        return {
            ...remainingDownloadFilters,
            ...(extended ? {
                rate: curMinRate ? parseFloat(curMinRate) : 0,
                ...(curMinNumbers ? { min_numbers: parseInt(curMinNumbers) } : {}),
                ...(curMinAtxNumbers ? { [minAtxDialersName]: parseInt(curMinAtxNumbers) } : {})
            } : {}),
            ...(downaloadSpKey ? { sp_key: downaloadSpKey } : {}),
            ...(!extended ? { sp_key_list: downloadSpKeyList } : {})
        };
    }, [remainingDownloadFilters, downaloadSpKey, filters, extended]);

    const downLoadParams = getDownloadParams();

    const downloadMethod = !extended ?
        service ? ACCESS_LIST_GET_LIST_API : SMS_ACCESS_LIST_GET_LIST_API :
        service ? ACCESS_LIST_EXTENDED_GET_LIST_API : SMS_ACCESS_LIST_EXTENDED_GET_LIST_API;
    const downloadFileName = !extended ? "accesses_list.csv" : "extended_accesses_list.csv";

    return (
        <>
            <PanelLayout>
                <GlobalStyle />
                <PageHeader title="Access List">
                    <ExportCsv
                        method={downloadMethod}
                        params={{
                            filter: downLoadParams,
                            ...(!extended ? { no_number: filters.no_number } : {})
                        }}
                        title="Download"
                        fileName={downloadFileName}
                        style={{ position: "relative", right: 15 }}
                    />
                </PageHeader>
                <FlexboxGrid>
                    <FlexboxGrid.Item colspan={24}>
                        <AccessListFilters
                            service={service}
                            onChange={onChangeFilters}
                            defaultFormValue={defaultFilter}
                            supplierList={supplierList}
                            currency_list={currency_list}
                            isMobile={isMobile}
                            extended={extended}
                            setExtended={setExtended}
                        />
                    </FlexboxGrid.Item>
                </FlexboxGrid>

                <AccessListTable
                    {...{
                        accessList,
                        service_plan_list,
                        accessListLoading,
                        page,
                        count,
                        perPage: currentPerPage,
                        service,
                    }}
                    paymentTermsList={paymentTermsList}
                    getItems={getItems}
                    onPerPageChange={setCurrentPerPage}
                    onSort={(column, type) => {
                        setSortState({ column, type })
                    }
                    }
                    sortType={sortState.type}
                    sortColumn={sortState.column}
                    filters={filters}

                    extended={extended}
                    setExtended={setExtended}
                />

            </PanelLayout>
        </>
    );
};

const mapState = ({ auth, access_list, references }) => ({
    accessList: access_list.items,
    count: access_list.count,
    service: auth.service,

    accessListLoading: access_list.loading,
    page: access_list.page,
    perPage: access_list.per_page,
    defaultSPKey: references.defaultSPKey,
    currency_list: references.currency_list,
    paymentTermsList: references.payment_terms_list,
    service_plan_list: references.service_plan_list.filter(servicePlanNoTrunkFilter),
    supplierList: !auth.service ? references["sms.supplier_list"] : []
});

export default connect(mapState, {
    getAccessList,
    getReferencesForReports
})(AccessList);

const GlobalStyle = createGlobalStyle`
    .rs-picker-check-menu-items {
        overflow-y: hidden !important;
        max-height: none !important;
    }
`;
